import {
  RealtimeEvent,
  type MessageCreatedEvent,
  type NotificationCreatedEvent,
  type TicketCreatedEvent,
  type TicketUpdatedEvent,
} from '@pe/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { connectSocket, disconnectSocket } from '../lib/socket';
import { useAuthStore } from '../stores/auth-store';

/**
 * Connecte la socket au token actuel et invalide les caches React Query
 * pertinents quand un évènement arrive. Conçu pour être appelé une fois
 * dans App.tsx, sous le QueryClientProvider.
 */
export function useRealtime(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const status = useAuthStore((s) => s.status);
  const qc = useQueryClient();

  useEffect(() => {
    if (status !== 'authenticated' || !accessToken) {
      disconnectSocket();
      return;
    }

    const socket = connectSocket(accessToken);

    const onTicketCreated = (_payload: TicketCreatedEvent) => {
      void qc.invalidateQueries({ queryKey: ['tickets'] });
    };

    const onTicketUpdated = (payload: TicketUpdatedEvent) => {
      void qc.invalidateQueries({ queryKey: ['tickets', 'detail', payload.ticketId] });
      void qc.invalidateQueries({ queryKey: ['tickets', 'list'] });
    };

    const onMessageCreated = (payload: MessageCreatedEvent) => {
      void qc.invalidateQueries({ queryKey: ['tickets', 'messages', payload.ticketId] });
      void qc.invalidateQueries({ queryKey: ['tickets', 'detail', payload.ticketId] });
    };

    const onNotificationCreated = (_payload: NotificationCreatedEvent) => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'list'] });
    };

    socket.on(RealtimeEvent.TICKET_CREATED, onTicketCreated);
    socket.on(RealtimeEvent.TICKET_UPDATED, onTicketUpdated);
    socket.on(RealtimeEvent.MESSAGE_CREATED, onMessageCreated);
    socket.on(RealtimeEvent.NOTIFICATION_CREATED, onNotificationCreated);

    return () => {
      socket.off(RealtimeEvent.TICKET_CREATED, onTicketCreated);
      socket.off(RealtimeEvent.TICKET_UPDATED, onTicketUpdated);
      socket.off(RealtimeEvent.MESSAGE_CREATED, onMessageCreated);
      socket.off(RealtimeEvent.NOTIFICATION_CREATED, onNotificationCreated);
    };
  }, [accessToken, status, qc]);
}
